import { Card, CardContent, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'

import {
  Collection,
  CollectionFeeDetailFragment,
  ConsoleOrderDetailQuery,
  FeeDetailFragment,
  Maybe,
  UserCartStoreItemConfirmationEventCollectionFragment,
  UserCartStoreItemPurchaseConfirmationFragment,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  OrderItemDetailSkeleton,
  PaymentInfo,
  ShippingAddress,
} from '@/components/organisms'
import { OrderItemCell } from '@/components/pages/StoreItemPurchaseProceed/template'
import {
  PurchaseDetailCell,
  PurchaseDetailSkeleton,
} from '@/components/pages/StorePurchaseOrderDetail/template'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  userId: string
  loading: boolean
  transactionOrder: Maybe<
    ConsoleOrderDetailQuery['artistUserCartTransactionOrder']
  >
  systemFee: Maybe<CollectionFeeDetailFragment>[]
  shippingFee: Maybe<FeeDetailFragment>
  konbiniFee: Maybe<FeeDetailFragment>
  purchaseType?: 'cash' | 'konbini' | 'prepaid_point'
  cartItemsByCollection:
    | {
        collection: Maybe<Pick<Collection, 'id' | 'title'>> | undefined
        items: UserCartStoreItemPurchaseConfirmationFragment['items']
        eventCollection:
          | UserCartStoreItemConfirmationEventCollectionFragment
          | undefined
      }[]
    | null
  formattedPrepaidPointBalance: string | number
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const ConsoleOrderDetailTemplate: Template<State, Action> = ({
  state,
}) => {
  const { t } = useTranslation('consoleOrderDetail')
  const { generatePath, routerPages, routerNames } = useRouter()

  // 個人情報の登録必須項目
  const personalInformationRequirements = useMemo<string[]>(() => {
    if (!state.transactionOrder) {
      return []
    }

    const result = state.transactionOrder.items.reduce(
      (acc: string[], item) => {
        const requirements = item.item.personalInformationRequirements

        return Array.isArray(requirements) ? acc.concat(requirements) : acc
      },
      []
    )

    return [...new Set(result)]
  }, [state.transactionOrder])

  const user = state.transactionOrder?.itemOrders?.[0]?.user

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleOrderDetail} />
      <ConsoleBaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: generatePath(routerPages.ConsoleUserOrders, {
            userId: state.userId ?? '#',
          }),
          title: t('title'),
        }}
      >
        <OrderDetailCell
          loading={state.loading}
          orderDate={state.transactionOrder?.formattedCreatedAt}
          transactionOrder={state.transactionOrder}
          cartItemsByCollection={state.cartItemsByCollection}
        />
        <PaymentInfo
          loading={state.loading}
          purchaseType={state.purchaseType}
          paymentSource={user?.credit}
          formattedPrepaidPointBalance={state.formattedPrepaidPointBalance}
        />
        <ShippingAddress
          loading={state.loading}
          fullName={user?.fullName ?? ''}
          address={user?.address}
          isPhoneNumberVerified={user?.isPhoneNumberVerified}
          isDeletedUser={user?.status === 'archived' || user?.status === 'left'}
          phoneNumber={user?.formattedPhoneNumber ?? ''}
          rightLink={generatePath(routerPages.ConsoleUserDetail, {
            userId: user?.id ?? '#',
          })}
          hideVerified
          personalInformationRequirements={personalInformationRequirements}
        />
        <PurchaseDetailCell
          loading={state.loading}
          systemFee={state.systemFee}
          shippingFee={state.shippingFee}
          konbiniFee={state.konbiniFee}
          paymentType={state.transactionOrder?.paymentType}
          totalPrice={state.transactionOrder?.originalTotalAmount ?? 0}
          formattedTotalPrice={
            state.transactionOrder?.formattedOriginalTotalAmount
          }
          purchaseType={state.purchaseType}
        />
        <SaleProfitCell
          loading={state.loading}
          formattedShippingFee={state.shippingFee?.formattedFee ?? '0'}
          formattedSaleProfit={
            state.transactionOrder?.formattedSaleProfit ?? '0'
          }
          formattedStoreFee={state.transactionOrder?.formattedStoreFee ?? '0'}
          formattedTotalAmountWithoutFee={
            state.transactionOrder?.formattedTotalAmountWithoutFee ?? '0'
          }
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type OrderDetailCellProps = {
  loading: boolean
  orderDate?: string | null
  transactionOrder: State['transactionOrder']
  cartItemsByCollection: State['cartItemsByCollection']
}
export const OrderDetailCell: Cell<OrderDetailCellProps> = ({
  loading,
  orderDate,
  transactionOrder,
  cartItemsByCollection,
}) => {
  const { t } = useTranslation('consoleOrderDetail')

  if (loading) return <OrderItemDetailSkeleton />

  return (
    <CellFrame sx={{ pb: 4 }}>
      <Typography variant="h5" color="text.secondary" sx={{ py: 3 }}>
        {t('orderDetail', '注文内容')}
      </Typography>
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ px: 5, py: 4, '&:last-child': { pb: 4 } }}>
          <Typography variant="body2" sx={{ pb: 3 }}>
            {t('orderDate', { date: orderDate })}
          </Typography>
          <Stack spacing={5}>
            {cartItemsByCollection?.map(({ items, collection }, index) => {
              // コレクションに紐づいている受け取り情報
              const pickupUser = transactionOrder?.pickupUsers?.find(
                ({ collectionId }) => collectionId === collection?.id
              )
              return (
                <Stack spacing={4} key={index}>
                  <Stack spacing={2}>
                    {collection && (
                      <Typography variant="h6">{collection?.title}</Typography>
                    )}
                    {pickupUser && (
                      <Typography variant="subtitle1">
                        {t('pickupTime')}
                        {pickupUser.formattedPickupStartEndTime}
                      </Typography>
                    )}
                  </Stack>
                  {items.map((cartItem, _index) => (
                    <OrderItemCell
                      key={_index}
                      item={cartItem.item}
                      quantity={cartItem.count}
                    />
                  ))}
                </Stack>
              )
            })}
          </Stack>
        </CardContent>
      </Card>
    </CellFrame>
  )
}

type SaleProfitCellProps = {
  loading: boolean
  formattedShippingFee?: string
  formattedStoreFee?: string
  formattedSaleProfit?: string
  formattedTotalAmountWithoutFee?: string
}
export const SaleProfitCell: Cell<SaleProfitCellProps> = ({
  loading,
  formattedShippingFee,
  formattedStoreFee,
  formattedSaleProfit,
  formattedTotalAmountWithoutFee,
}) => {
  const { t } = useTranslation('consoleOrderDetail')
  const data = [
    {
      label: t('commodityPrice', '商品代金：'),
      value: formattedTotalAmountWithoutFee,
    },
    {
      label: t('salePrice', '販売手数料：'),
      value: `-${formattedStoreFee}`,
    },
    { label: t('deliveryPrice', '配送料：'), value: formattedShippingFee },
  ]
  if (loading) {
    return <PurchaseDetailSkeleton />
  }
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Typography variant="h5" color="text.secondary" sx={{ py: 3 }}>
        {t('saleProfit', '販売利益')}
      </Typography>
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ px: 5, py: 3, '&:last-child': { pb: 3 } }}>
          {data.map(d => (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2" color="text.secondary">
                {d.label}
              </Typography>
              <Typography variant="body2">
                {t('itemPrice', '¥', { price: d.value })}
              </Typography>
            </Stack>
          ))}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">{t('saleProfit', '販売利益')}</Typography>
            <Typography variant="h6">
              {t('itemPrice', '¥', { price: formattedSaleProfit })}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </CellFrame>
  )
}
